import { MoreVertical, Edit, ToggleLeft, ToggleRight, Trash2, Circle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import type { Command } from "@/hooks/useCommands";

interface CommandCardProps {
  command: Command;
  onEdit: () => void;
  onToggleActive: () => void;
  onDelete: () => void;
}

const statusConfig = {
  available: {
    bg: "bg-emerald-50 border-emerald-200 dark:bg-emerald-950/30 dark:border-emerald-800",
    badge: "bg-emerald-500 hover:bg-emerald-500",
    label: "Livre",
    dot: "text-emerald-500",
  },
  in_use: {
    bg: "bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:border-amber-800",
    badge: "bg-amber-500 hover:bg-amber-500",
    label: "Em uso",
    dot: "text-amber-500",
  },
};

export function CommandCard({ command, onEdit, onToggleActive, onDelete }: CommandCardProps) {
  const config = statusConfig[command.status] || statusConfig.available;

  return (
    <Card
      className={cn(
        "relative transition-all hover:shadow-md cursor-pointer group border-2",
        command.is_active ? config.bg : "opacity-50 bg-muted border-muted"
      )}
    >
      <CardContent className="p-3">
        {/* Status Indicator */}
        {command.is_active && (
          <div className="absolute top-1 left-1">
            <Circle className={cn("h-2.5 w-2.5 fill-current", config.dot)} />
          </div>
        )}

        {/* Menu - show on hover */}
        <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-6 w-6">
                <MoreVertical className="h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={onEdit}>
                <Edit className="h-4 w-4 mr-2" />
                Editar
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onToggleActive}>
                {command.is_active ? (
                  <>
                    <ToggleLeft className="h-4 w-4 mr-2" />
                    Desativar
                  </>
                ) : (
                  <>
                    <ToggleRight className="h-4 w-4 mr-2" />
                    Ativar
                  </>
                )}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onDelete} className="text-destructive">
                <Trash2 className="h-4 w-4 mr-2" />
                Excluir
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Content */}
        <div className="text-center py-2">
          <span className="text-xl font-bold">{command.number}</span>
          {command.customer_name && (
            <p className="text-xs text-muted-foreground truncate">{command.customer_name}</p>
          )}
          {command.is_active && (
            <Badge className={cn("mt-1 text-white text-[10px] px-1.5 py-0", config.badge)}>
              {config.label}
            </Badge>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
